import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import ModuleNav from '../module/ModuleNav';
import { HiMenu, HiSearch, HiPlus, HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../components/toaster';
import {
  fetchModuleGroupingList,
  createModuleGrouping,
  updateModuleGrouping,
  deactivateModuleGrouping,
} from '../../services/moduleGroupingService';

const PAGE_SIZE = 50;

const emptyForm = () => ({
  name: '',
  is_active: true,
  order: '',
});

const StatusBadge = ({ isActive }) => (
  <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="40" stroke="#A855F7" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" />
      </svg>
    </div>
  </div>
);

const ModuleGroupings = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [itemToDeactivate, setItemToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const searchTimerRef = useRef(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 0;

  const loadData = useCallback(async (searchText = '', page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchModuleGroupingList(page, PAGE_SIZE, searchText);
      const items = Array.isArray(res?.data) ? res.data : [];
      setData(items);
      setTotalCount(res?.totalCount ?? items.length);
    } catch (err) {
      setError(err.message || 'Error loading module groupings.');
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    loadData(debouncedSearch, currentPage);
  }, [debouncedSearch, currentPage, loadData]);

  const openAdd = () => {
    setFormData(emptyForm());
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    setFormData({
      name: item.name || '',
      is_active: item.is_active !== false,
      order: item.order === 0 || item.order ? String(item.order) : '',
    });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(item);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setFormData(emptyForm());
    setSelectedItem(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const name = String(formData.name || '').trim();
    const errs = {};
    if (!name) errs.name = 'Name is required';
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    const payload = {
      name,
      is_active: formData.is_active,
    };
    if (isEditing && formData.order !== '') {
      const order = parseInt(formData.order, 10);
      if (Number.isFinite(order) && order >= 0) payload.order = order;
    }

    setSaving(true);
    setFormGeneralError('');
    try {
      if (isEditing) {
        await updateModuleGrouping({ module_grouping_id: selectedItem._id, ...payload });
        Toast.success('Module grouping updated');
      } else {
        await createModuleGrouping(payload);
        Toast.success('Module grouping created');
      }
      closeForm();
      await loadData(debouncedSearch, isEditing ? currentPage : 1);
      if (!isEditing) setCurrentPage(1);
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to save module grouping');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async () => {
    if (!itemToDeactivate) return;
    setDeactivating(true);
    try {
      await deactivateModuleGrouping(itemToDeactivate._id);
      Toast.success('Module grouping deactivated');
      setShowDeactivateModal(false);
      setItemToDeactivate(null);
      await loadData(debouncedSearch, currentPage);
    } catch (err) {
      Toast.error(err.message || 'Failed to deactivate');
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/50">
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">Module Management</h1>
            </div>
          </div>
          <ModuleNav />

          <div className="mb-4 flex flex-col md:flex-row md:justify-between gap-3">
            <div className="relative w-full md:w-64">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search groupings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
              />
            </div>
            <button onClick={openAdd} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl bg-brand hover:opacity-90 self-end">
              <HiPlus className="w-5 h-5" />
              Add grouping
            </button>
          </div>

          {isLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No module groupings yet</h3>
                <p className="text-sm text-gray-500 mb-6">Add a grouping, then assign it on Module like tags.</p>
                <button onClick={openAdd} className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-brand">
                  <HiPlus className="w-4 h-4" /> Add grouping
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {error ? (
                <div className="px-4 py-6 text-center text-red-500">{error}</div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Order</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.map((item) => (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-800">{item.name}</td>
                          <td className="px-4 py-3 text-gray-600">{item.order ?? 0}</td>
                          <td className="px-4 py-3"><StatusBadge isActive={item.is_active} /></td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg" style={{ backgroundColor: '#F2E2F7' }}>
                                <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                              <button
                                onClick={() => { setItemToDeactivate(item); setShowDeactivateModal(true); }}
                                className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg"
                                title="Deactivate"
                              >
                                <MdDelete className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {totalPages > 1 && (
                <div className="px-4 py-3 flex justify-end gap-1 border-t">
                  <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg disabled:opacity-40">
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg disabled:opacity-40">
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b font-bold">{isEditing ? 'Edit grouping' : 'Add grouping'}</div>
            <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1 text-sm">
              {formGeneralError && <div className="p-3 bg-red-50 text-red-700 rounded-lg">{formGeneralError}</div>}
              <label className="block text-xs font-medium">Name *</label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                }}
              />
              {formErrors.name && <p className="text-xs text-red-600">{formErrors.name}</p>}
              {isEditing && (
                <>
                  <label className="block text-xs font-medium">Order</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border rounded-lg px-3 py-2"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  />
                </>
              )}
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_active}
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    formData.is_active ? 'bg-brand' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white rounded-lg bg-brand disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-800 mb-2">Deactivate this grouping?</h3>
            <p className="text-sm text-gray-500 mb-4">It stays on modules but will not match Because you until you activate it again. No hard delete.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDeactivateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={confirmDeactivate} disabled={deactivating} className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg">Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleGroupings;
